import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from 'react-redux';
import _, { debounce } from 'lodash-es';
import { AppConstant } from "../../../Assests/AppConstant";
import {
    validateRequired,
    validateLength,
    validateEmail,
    validateContactNum,
    validateFieldIsNum,
    validatePassword,
    validateConfirmPassword,
    validateCarrierList,
    validateFieldMinValue,
    validateFieldIsText,
    validateStringIsOnlyNumber
} from "../../../../Utils/validationFunction";
import { toast } from "react-toastify";
import { showAlert } from "../../../Assests/Utility";
import FileUpload from "../CommanFileUpload/fileUpload";
import "./CustomDynamicFormSS.css";
import PropTypes from 'prop-types';
import { sagaActions } from "../../../../sagas/sagaActions";
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { getFileUploadReducer } from "../../../../redux/reducers/reducerSlices/FileuploadReducer";
import CustomFieldWithTtileSS, { INVALID_DATE_SENTINEL } from "../CustomField/CustomFieldWithTtileSS";
import PhoneInput from "react-phone-input-2";
import 'react-phone-input-2/lib/style.css';
import parsePhoneNumber from "libphonenumber-js";

/**
 * Validates a YYYY-MM-DD string produced by a native <input type="date">.
 *
 * @param {string}  dateStr    - YYYY-MM-DD, INVALID_DATE_SENTINEL, or ''
 * @param {boolean} isRequired - whether the field is required
 * @returns {string} error message, or '' when valid
 */
const validateDateInput = (dateStr, isRequired) => {
    // Sentinel: browser signalled a validity failure (rangeOverflow, badInput, etc.)
    if (dateStr === INVALID_DATE_SENTINEL) {
        return 'Please enter a valid date (MM/DD/YYYY).';
    }
    // Empty
    if (!dateStr) {
        return isRequired ? 'This field is required.' : '';
    }
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        // Reject years outside the 4-digit range
        if (isNaN(year) || parts[0].length !== 4 || year < 1000 || year > 9999) {
            return 'Year must be a valid 4-digit year (e.g. 2025).';
        }
        // Calendar-correctness: rejects e.g. April 31, Feb 30
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);
        const dateObj = new Date(`${dateStr}T00:00:00`);
        if (
            isNaN(dateObj.getTime()) ||
            dateObj.getMonth() + 1 !== month ||
            dateObj.getDate() !== day
        ) {
            return `${parts[1]}/${parts[2]}/${parts[0]} is not a valid date.`;
        }
    }
    return '';
};

/**
 * Reusable form component with New UI.
 *
 * Date field support (type="date"):
 *   Add validateDate: true to formValidation to enable 4-digit year enforcement
 *   and calendar-correctness validation (e.g. 04/31/2025 is rejected).
 *
 *   IMPORTANT — how date validation state is tracked:
 *   When the browser signals a validity failure for a date input (e.g. user
 *   typed year 22222 causing rangeOverflow, or an impossible day causing
 *   badInput), CustomFieldWithTtileSS calls onChange with INVALID_DATE_c.
 *
 *   We store this sentinel ONLY in dateValidityRef (a separate Map), NOT in
 *   `inputs` state.  Storing it in `inputs` would feed it back as the
 *   controlled value prop, causing React to re-render the input with value=""
 *   which would fire another onChange with val="" badInput=false, overwriting
 *   the sentinel with "" — an infinite loop.
 *
 *   `inputs` always holds either "" (empty/cleared) or a valid YYYY-MM-DD.
 *   `dateValidityRef` holds INVALID_DATE_SENTINEL for fields that have bad
 *   input, or "" for fields that are clean.
 *
 *   validateAndSetErrors() reads dateValidityRef for validateDate fields,
 *   falling back to inputs[control] when no sentinel is present.
 */
export const CustomDynamicFormSS = ({
    formFieldList, sort, submitbtn, cancelBtn, themeSelected,
    getSummitedFormData, getFormFieldData, editFormData, loading,
    setParticularFieldValue, disclaimer, disclaimersArray,
    customDisableSave = false, customBtn, customBtnHandler,
    customBtnLoading = false, customBtnSubmit = false,
    searchableSelectFormData, resetFormFields, getCancelBtnClick
}) => {
    const fileUploadData = useSelector(getFileUploadReducer);
    const dispatch = useDispatch();

    let fieldArray = [...formFieldList];
    const [inputs, setInputs] = useState({});
    const [imageUploading, setImageUploading] = useState({ loading: false, name: "" });
    const debounceFn = useCallback(debounce(handleDebounceFn, 1000), []);
    const [isValid, setValid] = useState(false);
    const [phoneInputKey, setPhoneInputKey] = useState(Date.now());

    /**
     * dateValidityRef stores INVALID_DATE_SENTINEL for date fields that have
 * browser-detected bad input (rangeOverflow, badInput, etc.).
     * Keys are field names; value is INVALID_DATE_SENTINEL or "".
     * This is separate from `inputs` to avoid the controlled-input feedback loop.
   */
    const dateValidityRef = useRef({});

    const handleValid = (value) => { setValid(value); };
    const [initialLoad, setInitialLoad] = useState(false);
    let [errors, setErrors] = useState({});
    let [dirty, setDirty] = useState({});

    const setInitialValidation = () => {
        let errorDataList = {};
        let dirtyData = {};
        let inputData = {};
        fieldArray.forEach((control) => {
            inputData[control.name] = "";
            errorDataList[control.name] = [];
            dirtyData[control.name] = false;
        });
        // Reset date validity tracking
        dateValidityRef.current = {};
        setInputs(JSON.parse(JSON.stringify(inputData)));
        setErrors(JSON.parse(JSON.stringify(errorDataList)));
        setDirty(JSON.parse(JSON.stringify(dirtyData)));
        setInitialLoad(true);
    };

    function handleDebounceFn(value) {
        if (handleValid) handleValid(value);
    }

    useEffect(() => {
        setInitialValidation();
    }, []);

    useEffect(() => {
        if (editFormData) {
            // Clear any stale date validity when edit data loads
            dateValidityRef.current = {};
            setInputs(editFormData);
        }
    }, [editFormData, initialLoad]);

    useEffect(() => {
        if (setParticularFieldValue != null && Object.keys(setParticularFieldValue)?.length > 0) {
            setInputs((prev) => ({ ...prev, ...setParticularFieldValue }));
        }
    }, [setParticularFieldValue, initialLoad]);

    let handleChange = (data, name, control, searchableSelectFormData) => {
        if (typeof data === 'object') {
            data = data.toString();
        }

        // Handle INVALID_DATE_SENTINEL from date inputs separately:
        // Store sentinel in dateValidityRef but store "" in inputs state so
        // the controlled input does not receive the sentinel as its value
        // (which would cause React to clear it and trigger another onChange).
        if (data === INVALID_DATE_SENTINEL) {
            dateValidityRef.current = { ...dateValidityRef.current, [name]: INVALID_DATE_SENTINEL };
            getFormFieldData && getFormFieldData("", name, control, inputs, searchableSelectFormData);
            // Do NOT update inputs state with the sentinel
            return;
        }

        // For any valid date value (YYYY-MM-DD) or other field types,
        // clear the date validity sentinel if previously set.
        // IMPORTANT: only clear the sentinel when we receive a real non-empty value.
        // When data === "" (browser cleared the field), the field is either genuinely
        // empty (user cleared it) OR still bad (Chrome silent-clear for impossible dates
        // like Feb 31 where badInput stays false after a prior badInput event).
        // Clearing the sentinel on "" would overwrite a valid "bad input" detection that
        // was set during an earlier onChange (e.g. partial entry with badInput=true) and
        // produce "This field is required." instead of the proper date-validation message.
        if (data && dateValidityRef.current[name] === INVALID_DATE_SENTINEL) {
            dateValidityRef.current = { ...dateValidityRef.current, [name]: "" };
        }
        // When data is "" and the sentinel is set, the field is still invalid — leave it.
        // When data is "" and there is no sentinel, the field was genuinely cleared — leave
        // dateValidityRef alone (it is already "" or absent, which is correct).

        if ((name === 'contact_number' || name === 'phone_number') && data.length > 14) {
            const trimmed = data.substring(0, 14);
            getFormFieldData && getFormFieldData(trimmed, name, control, inputs, searchableSelectFormData);
            setInputs((inputs) => ({ ...inputs, [name]: trimmed }));
        } else {
            getFormFieldData && getFormFieldData(data, name, control, inputs, searchableSelectFormData);
            setInputs((inputs) => ({ ...inputs, [name]: data }));
        }
    };

    const handleErrors = (name) => {
        setDirty((dirty) => ({ ...dirty, [name]: true }));
        validateAndSetErrors();
    };

    /**
     * Core validation.
     *
   * For fields with validateDate:true, reads dateValidityRef first:
     *  - If sentinel is present → bad input → show "Please enter a valid date"
  *  - Otherwise fall through to normal validateDateInput with inputs[control]
     *
     * @returns {boolean} true if all fields valid
     */
    const validateAndSetErrors = () => {
        const currentInputs = inputs;
        let validArray = [];
        let errorData = {};
        let formValidation = {};

        fieldArray.forEach((control) => {
            errorData[control.name] = [];
            formValidation[control.name] = control.formValidation;
        });

        Object.keys(errorData).forEach((control) => {
            let valid = true;

            if (formValidation[control]?.validateDate) {
                const isRequired = !!formValidation[control]?.required;
                // Check sentinel first (bad input detected by browser)
                const sentinel = dateValidityRef.current[control];
                const dateStr = sentinel === INVALID_DATE_SENTINEL
                    ? INVALID_DATE_SENTINEL
                    : currentInputs[control] || "";
                const dateErr = validateDateInput(dateStr, isRequired);
                if (dateErr) {
                    errorData[control] = [dateErr];
                    valid = false;
                }
                validArray.push(valid);
                return; // skip other validators for this field
            }

            if (formValidation[control]?.required) {
                valid = validateRequired(control, currentInputs[control], errorData);
            }

            if (currentInputs[control]) {
                if (formValidation[control]?.lengthValidation) {
                    valid = validateLength(
                        control,
                        currentInputs[control],
                        errorData,
                        formValidation[control]?.lengthValidation?.minLength,
                        formValidation[control]?.lengthValidation?.maxLength,
                        formValidation[control]?.lengthValidation.customError
                    );
                }
                if (formValidation[control]?.validateEmail) {
                    valid = validateEmail(control, currentInputs[control], errorData);
                }
                if (formValidation[control]?.validateContactNum) {
                    valid = validateContactNum(
                        control,
                        currentInputs[control],
                        errorData,
                        formValidation[control]?.lengthValidation?.minLength
                    );
                }
                if (formValidation[control]?.checkPassword) {
                    valid = validatePassword(control, currentInputs[control], errorData);
                }
                if (formValidation[control]?.confirmPassword) {
                    valid = validateConfirmPassword(
                        control,
                        currentInputs[control],
                        errorData,
                        formValidation[control].passwordText
                    );
                }
                if (formValidation[control]?.allowOnlyNumber) {
                    valid = validateFieldIsNum(control, currentInputs[control], errorData);
                }
                if (formValidation[control]?.allowOnlyText) {
                    valid = validateFieldIsText(control, currentInputs[control], errorData);
                }
                if (formValidation[control]?.validateMinValue) {
                    valid = validateFieldMinValue(
                        control,
                        currentInputs[control],
                        errorData,
                        formValidation[control]?.validateMinValue.minValue
                    );
                }
                if (formValidation[control]?.carrier_name || formValidation[control]?.yardLocation) {
                    valid = validateCarrierList(control, currentInputs[control], errorData);
                }
                if (formValidation[control]?.numberType) {
                    valid = validateStringIsOnlyNumber(control, currentInputs[control], errorData);
                }
            }
            validArray.push(valid);
        });

        setErrors(errorData);
        return !validArray.includes(false);
    };

    let validate = validateAndSetErrors;

    useEffect(() => {
        if (fileUploadData?.data) {
            setImageUploading({ loading: false, name: "" });
            if (fileUploadData.data.name) {
                setInputs((prev) => ({
                    ...prev,
                    [fileUploadData.data.name]: fileUploadData?.data?.fileUrl,
                }));
                dispatch({ type: sagaActions.RESET_FILE_UPLOAD });
            }
        } else {
            if (fileUploadData?.error) {
                setImageUploading({ loading: false, name: "" });
                showAlert(toast.TYPE.ERROR, fileUploadData.error.message);
            }
        }
    }, [fileUploadData]);

    const onFileChange = async (fileData, name) => {
        setImageUploading({ loading: true, name: name });
        const fd = new FormData();
        fd.append("file", fileData);
        dispatch({ type: sagaActions.FILE_UPLOAD, payload: { name, fd } });
    };

    const submitForm = async () => {
        const allDirty = {};
        Object.keys(dirty).forEach((control) => { allDirty[control] = true; });
        setDirty(allDirty);
        if (validate()) {
            getSummitedFormData(inputs);
        }
    };

    const submitFormCustomBtn = async () => {
        const allDirty = {};
        Object.keys(dirty).forEach((control) => { allDirty[control] = true; });
        setDirty(allDirty);
        if (validate()) {
            customBtnHandler(inputs);
        }
    };

    const cancelForm = () => {
        if (editFormData && Object.keys(editFormData).length > 0) {
            if (resetFormFields && Array.isArray(resetFormFields)) {
                let newInputs = { ...inputs };
                let newDirty = { ...dirty };
                let newErrors = { ...errors };
                resetFormFields.forEach(fieldName => {
                    newInputs[fieldName] = "";
                    newDirty[fieldName] = false;
                    newErrors[fieldName] = [];
                    delete dateValidityRef.current[fieldName];
                });
                setInputs(newInputs);
                setDirty(newDirty);
                setErrors(newErrors);
                setPhoneInputKey(Date.now());
            } else {
                dateValidityRef.current = {};
                setInputs(editFormData);
                let dirtyData = {};
                let errorData = {};
                fieldArray.forEach((control) => {
                    dirtyData[control.name] = false;
                    errorData[control.name] = [];
                });
                setDirty(dirtyData);
                setErrors(errorData);
                setPhoneInputKey(Date.now());
            }
            if (getCancelBtnClick) getCancelBtnClick();
        } else {
            setPhoneInputKey(Date.now());
            setInitialValidation();
            if (getCancelBtnClick) getCancelBtnClick();
        }
    };

    function handleValidation(valid) { debounceFn(valid); }

    const checkValidPhone = (value, country) => {
        value = value.slice(country.dialCode.length);
        const parsedNumber = parsePhoneNumber(value, country.iso2.toUpperCase());
        let valid = true;
        const isZero = value.length < 2;
        if (!isZero) { valid = parsedNumber?.isValid(); }
        handleValidation(isZero ? false : valid);
        if (!valid && !isZero) {
            return 'Invalid phone: ' + value + ', ' + country.name;
        } else {
            return true;
        }
    };

    sort && fieldArray.sort((a, b) => a.position - b.position);

    const selectUI = (control) => {
        switch (control.type) {
            case "file":
                return (
                    <FileUpload
                        showError={dirty[control.name] && errors[control.name][0] ? errors[control.name] : ""}
                        accept={[control.accept]}
                        title={control.title}
                        name={control.name}
                        sizeConfig={AppConstant.help.imageTypen}
                        onFileChange={onFileChange}
                        isLoading={imageUploading}
                        showImageLogo={true}
                        imageSrc={inputs[control.name]}
                    />
                );
            case "select_with_input_search_add_button_create_po_SS":
                return (
                    <>
                    <div className={`customInputTitle ${control.parentClass}`}>
                        <div>
                            {control.titleIcon && <i className={control.titleIconClass}></i>} {control.inputTitle} {control.formValidation.required && <span className="color-red">*</span>}</div>
                            {control.isEditProcess ==="true" ?
                            <>
                                {!control.hideInForm  ?
                                    <>
                                    <Select
                                        className={`basic-single ${control.componentClassName}`}
                                        classNamePrefix={control.classNamePrefix}
                                        placeholder={`${control.placeholderText}`}
                                        styles={control.customStylesLight}
                                        onInputChange={control.onInputChange}
                                        onChange={(data) => handleChange(data ? data.value : "", control.name, control)}
                                        onBlur={() => handleErrors(control.name)}
                                        defaultValue={control.defaultValue}
                                        isLoading={control.isLoading}
                                        isClearable={control.isClearable}
                                        isSearchable={control.isSearchable}
                                        options={control.options}
                                        name={control.name}
                                        formatCreateLabel={control.formatCreateLabelText}
                                        loadingMessage={control.loadingMessage}
                                        required={control?.formValidation?.required}
                                        isDisabled={control?.disabled}
                                    />
                                    <span className="errorMessage">
                                        {dirty[control.name] && errors[control.name][0] ? errors[control.name] : ""}
                                    </span>
                                    </>
                                    :
                                    <>
                                        <input type="text" value={control.defaultValue.label ? control.defaultValue.label: "" } className="w-100 customTextInput"  style={{ cursor: "not-allowed" }} disabled/>
                                    </>
                                }
                            </>
                            :
                            <>
                                <Select
                                        className={`basic-single ${control.componentClassName}`}
                                        classNamePrefix={control.classNamePrefix}
                                        placeholder={`${control.placeholderText}`}
                                        styles={control.customStylesLight}
                                        onInputChange={control.onInputChange}
                                        onChange={(data) => handleChange(data ? data.value : "", control.name, control)}
                                        onBlur={() => handleErrors(control.name)}
                                        defaultValue={control.defaultValue}
                                        isLoading={control.isLoading}
                                        isClearable={control.isClearable}
                                        isSearchable={control.isSearchable}
                                        options={control.options}
                                        name={control.name}
                                        formatCreateLabel={control.formatCreateLabelText}
                                        loadingMessage={control.loadingMessage}
                                        required={control?.formValidation?.required}
                                        isDisabled={control?.disabled}
                                    />
                                    <span className="errorMessage">
                                        {dirty[control.name] && errors[control.name][0] ? errors[control.name] : ""}
                                    </span>
                            </>}
                         </div>
                    </>
                    
                );
            case "select_with_input_search_add_button":
                return (
                    <>
                        <div className={`customInputTitle ${control.parentClass}`}>
                            <div>{control.titleIcon && <i className={control.titleIconClass}></i>} {control.inputTitle}</div>
                            <Select
                                className={`basic-single ${control.componentClassName}`}
                                classNamePrefix={control.classNamePrefix}
                                placeholder={`${control.placeholderText}`}
                                styles={control.customStylesLight}
                                onInputChange={control.onInputChange}
                                onChange={(data) => handleChange(data ? data.value : "", control.name, control)}
                                onBlur={() => handleErrors(control.name)}
                                defaultValue={control.defaultValue}
                                isLoading={control.isLoading}
                                isClearable={control.isClearable}
                                isSearchable={control.isSearchable}
                                options={control.options}
                                name={control.name}
                                formatCreateLabel={control.formatCreateLabelText}
                                loadingMessage={control.loadingMessage}
                                required={control?.formValidation?.required}
                            />
                        </div>
                        <span className="errorMessage">
                            {dirty[control.name] && errors[control.name][0] ? errors[control.name] : ""}
                        </span>
                    </>
                );
            case "select_with_input_search_add_button_temp":
                return (
                    <>
                        <div className={`customInputTitle ${control.parentClass}`}>
                            <div>
                                {control.titleIcon && <i className={control.titleIconClass}></i>} {control.inputTitle}
                                {control?.formValidation?.required ? <span className="text-red"> *</span> : ''}
                            </div>
                            <CreatableSelect
                                className={`basic-single ${control.componentClassName}`}
                                classNamePrefix={control.classNamePrefix}
                                placeholder={`${control.placeholderText}`}
                                styles={control.customStylesLight}
                                onInputChange={control.onInputChange}
                                onChange={(data) => handleChange(data ? data.value : "", control.name, control, data)}
                                onBlur={() => handleErrors(control.name)}
                                defaultValue={control.defaultValue}
                                isLoading={control.isLoading}
                                isClearable={control.isClearable}
                                isSearchable={control.isSearchable}
                                options={control.options}
                                name={control.name}
                                formatCreateLabel={control.formatCreateLabelText}
                                loadingMessage={control.loadingMessage}
                                required={control?.formValidation?.required}
                                onCreateOption={control?.handleCreateOption}
                                value={control?.searchableSelectFormData}
                                isDisabled={control?.disabled}
                            />
                        </div>
                        <span className="errorMessage">
                            {dirty[control.name] && errors[control.name][0] ? errors[control.name] : ""}
                        </span>
                    </>
                );
            case "select_with_input_search_add_button_withoutcreate":
                return (
                    <>
                        <div className={`customInputTitle ${control.parentClass}`}>
                            <div>{control.inputTitle} {control.formValidation.required && <span className="color-red">*</span>}</div>
                            <Select
                                className={`basic-single ${control.componentClassName}`}
                                classNamePrefix={control.classNamePrefix}
                                placeholder={`${control.placeholderText}`}
                                styles={control.customStylesLight}
                                onInputChange={control.onInputChange}
                                onChange={(data) => handleChange(data ? data.value : "", control.name, control)}
                                onBlur={() => handleErrors(control.name)}
                                defaultValue={control.defaultValue}
                                isLoading={control.isLoading}
                                isClearable={control.isClearable}
                                isSearchable={control.isSearchable}
                                options={control.options}
                                name={control.name}
                                formatCreateLabel={control.formatCreateLabelText}
                                loadingMessage={control.loadingMessage}
                                required={control?.formValidation?.required}
                                value={control?.searchableSelectFormData}
                                isDisabled={control?.disabled}
                            />
                        </div>
                        <span className="errorMessage">
                            {dirty[control.name] && errors[control.name][0] ? errors[control.name] : ""}
                        </span>
                    </>
                );
            case "phone_with_country_code":
                return (
                    <>
                        <div className={`inputTitle ${control.parentClass}`}>
                            <div>{control.title} {control.formValidation.required && <span className="color-red">*</span>}</div>
                            <PhoneInput
                                key={phoneInputKey}
                                isValid={checkValidPhone}
                                country={'us'}
                                onlyCountries={control.activeCountriesListCodes}
                                value={inputs[control.name] ? inputs[control.name] : ""}
                                inputClass="form-control"
                                placeholder={control.placeHolder}
                                countryCodeEditable={false}
                                onChange={(data) => handleChange(data ? data : "", control.name, control)}
                                containerClass={"form-group"}
                            />
                        </div>
                        <span className="errorMessage">
                            {dirty[control.name] && errors[control.name][0] ? errors[control.name] : ""}
                        </span>
                    </>
                );
            default:
                return (
                    <CustomFieldWithTtileSS
                        type={control.type}
                        showError={
                            dirty[control.name] && errors[control.name][0]
                                ? errors[control.name]
                                : ""
                        }
                        hideElement={control.hideElement}
                        name={control.name}
                        radiconfig={control.radiconfig}
                        customDatePickerConfig={control.customDatePickerConfig}
                        value={inputs[control.name] ? inputs[control.name] : ""}
                        handleErrors={handleErrors}
                        title={control.title}
                        required={control?.formValidation?.required}
                        onChange={(data, name) => handleChange(data, name, control)}
                        listData={control.getSelectData}
                        placeHolder={control.placeholder}
                        disabledField={control.disabledField}
                        addIcon={control?.formValidation?.addIcon}
                        iconHandler={control?.formValidation?.iconHandler}
                        className={control?.formValidation?.className}
                        titleIcon={control.titleIcon}
                        titleIconClass={control.titleIconClass}
                        editData={editFormData}
                        isEditDisable={control.isEditDisable}
                        optionsList={control.optionsList ? control.optionsList : null}
                        timeSlotStart={control.timeSlotStart ? control.timeSlotStart : "NA"}
                    />
                );
        }
    };

    return (
        <div className="container-fluid py-2" themeSelected={themeSelected} data-testid={AppConstant.dataTestId.customDynamicForm}>
            <div className="row gy-2">
                {fieldArray && fieldArray.map((control, index) => (
                    <div className={control.size} key={index}>
                        <div className="">
                            {selectUI(control, index)}
                        </div>
                    </div>
                ))}
            </div>
            {disclaimer && (
                <div className="my-3 subText color-red">
                    {disclaimersArray && disclaimersArray.map((control, index) => (
                        <>{control.text && (
                            <div className="row" key={index}>
                                <div className="col-12">
                                    <span className="color-red pe-1">*</span>
                                    <span>{control.text}</span>
                                </div>
                            </div>
                        )}</>
                    ))}
                </div>
            )}
            <div className="d-flex justify-content-end marbt-10 mt-3">
                {cancelBtn && (
                    <button
                        type="button"
                        className={`${cancelBtn.className} btn-unstyled ${themeSelected}`}
                        onClick={(e) => cancelForm(e, "close-btn")}
                    >
                        {cancelBtn.text}
                    </button>
                )}
                {customBtn && (
                    <button
                        type="button"
                        className={`btn-unstyled ${themeSelected} ${customBtn.className}`}
                        onClick={(e) => {
                            if (!customBtnSubmit) {
                                customBtnHandler(e, "customBtn");
                            } else {
                                submitFormCustomBtn(e, "close-btn");
                            }
                        }}
                    >
                        {customBtnLoading && (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" data-testid={AppConstant.dataTestId.loading} />
                        )}
                        {customBtnLoading ? AppConstant.commonStrings.loading : customBtn.text}
                    </button>
                )}
                {submitbtn && (
                    <button
                        type="button"
                        className={`${submitbtn?.className} btn-styled ${themeSelected}`}
                        onClick={(e) => submitForm(e, "close-btn")}
                        disabled={loading | imageUploading | customDisableSave}
                    >
                        {loading && (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" data-testid={AppConstant.dataTestId.loading} />
                        )}
                        {loading ? AppConstant.commonStrings.loading : submitbtn.text}
                    </button>
                )}
            </div>
        </div>
    );
};


CustomDynamicFormSS.propTypes = {
    formFieldList: PropTypes.array.isRequired,
    sort: PropTypes.bool,
    submitbtn: PropTypes.object.isRequired,
    cancelBtn: PropTypes.object,
    getSummitedFormData: PropTypes.func.isRequired,
    themeSelected: PropTypes.string.isRequired,
    getFormFieldData: PropTypes.func,
    editFormData: PropTypes.object,
    loading: PropTypes.bool,
    resetFormData: PropTypes.number
};
